import * as React from "react"

import { cn } from "@/lib/utils"
import { isoToUsDate, usDateToIso, maskUsDate } from "../../utils/dates"

const BASE_CLASS =
  "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"

/**
 * MM/DD/YYYY date field — what every `<Input type="date" />` in the app
 * actually renders.
 *
 * A native date input is drawn in the *browser's* locale, so the same value
 * read 08/01/2026 for one user and 01/08/2026 for another (and rendered blank
 * for legacy values stored as full ISO timestamps). This types and displays US
 * format everywhere while keeping the native contract: `onChange` receives an
 * event whose `target.value` is an ISO `YYYY-MM-DD` string, and `min` / `max`
 * still apply (out-of-range entries clamp to the boundary).
 */
const UsDateInput = React.forwardRef(
  ({ className, value, onChange, onBlur, min, max, placeholder, ...props }, ref) => {
    const [text, setText] = React.useState(isoToUsDate(value));

    React.useEffect(() => { setText(isoToUsDate(value)); }, [value]);

    const emit = (iso) => onChange?.({ target: { value: iso }, currentTarget: { value: iso } });

    const clamp = (iso) => {
      if (min && iso < min) return min;
      if (max && iso > max) return max;
      return iso;
    };

    const handleChange = (e) => {
      const masked = maskUsDate(e.target.value);
      setText(masked);
      if (!masked) {
        emit('');
        return;
      }
      const iso = usDateToIso(masked);
      if (!iso) return;
      // Deliberately NOT clamped here: when min/max point at a sibling field
      // (a start/end range), clamping each keystroke rewrites what the user
      // is typing. Out-of-range values are pulled back on blur instead.
      emit(iso);
    };

    // Drop a half-typed or impossible date rather than leaving text on screen
    // the parent never received, and pull an out-of-range date to its bound.
    const handleBlur = (e) => {
      const iso = usDateToIso(text);
      if (iso) {
        const clamped = clamp(iso);
        if (clamped !== iso) emit(clamped);
        setText(isoToUsDate(clamped));
      } else {
        setText(isoToUsDate(value));
      }
      onBlur?.(e);
    };

    return (
      <input
        type="text"
        inputMode="numeric"
        autoComplete="off"
        placeholder={placeholder || "MM/DD/YYYY"}
        maxLength={10}
        className={cn(BASE_CLASS, className)}
        value={text}
        onChange={handleChange}
        onBlur={handleBlur}
        ref={ref}
        {...props} />
    );
  }
)
UsDateInput.displayName = "UsDateInput"

const Input = React.forwardRef(({ className, type, ...props }, ref) => {
  if (type === "date") {
    return <UsDateInput className={className} ref={ref} {...props} />;
  }
  return (
    <input
      type={type}
      className={cn(BASE_CLASS, className)}
      ref={ref}
      {...props} />
  );
})
Input.displayName = "Input"

export { Input, UsDateInput }
